import Localization from "aldoo-ra/Localization"

/**
 * Safely clones objects using JSON parse/stringify to avoid issues with functions/JSX elements
 * @param {Object} obj - The object to clone
 * @returns {Object} A deep clone of the input object
 */
const safeClone = (obj) => {
  try {
    // Always use JSON parse/stringify for CMS data to avoid issues with JSX/functions
    return JSON.parse(JSON.stringify(obj))
  } catch (error) {
    console.warn('Failed to clone CMS data, returning original object:', error)
    return obj
  }
}

/**
 * Custom hook for CMS localization functionality
 * @returns {Object} Object containing readLocalization and resolveLocalization functions
 */
const useCMSLocalization = () => {
  const { load, t, translateWithLanguage, currentLanguage, getLanguage } = Localization()

  /**
   * Recursively extracts localization keys from an item
   * @param {Object} item - The item to extract keys from
   * @returns {Array<string>} Array of unique localization keys
   */
  const extractLocalizationKeys = (item) => {
    const keys = new Set()

    const extractKeys = (obj) => {
      if (!obj) return

      if (Array.isArray(obj.localization)) {
        obj.localization.forEach(({ localizationKey }) => {
          keys.add(localizationKey)
        })
      }

      Object.values(obj).forEach((value) => {
        if (value && typeof value === "object") {
          extractKeys(value)
        }
      })
    }

    extractKeys(item)
    return Array.from(keys)
  }

  /**
   * Loads localization data for the provided items
   * @param {Array<Object>} data - Array of items requiring localization
   */
  const readLocalization = async (data) => {
    const keysToLoad = new Set()

    data.forEach((item) => {
      const keys = extractLocalizationKeys(item)
      keys.forEach((key) => keysToLoad.add(key))
    })

    const uniqueKeys = Array.from(keysToLoad)
    await load(uniqueKeys)

    // Load English fallback for non-English languages
    // Skip on client-side if we have SSR data (to prevent API requests)
    // Always load on server-side for proper translation fallbacks
    const isServerSide = typeof window === 'undefined'
    const hasSSRData = !isServerSide && (
      window.__INITIAL_LOCALIZATION_DATA__ || 
      window.__HAD_INITIAL_LOCALIZATION_DATA__
    )
    
    if (getLanguage() !== "en" && (isServerSide || !hasSSRData)) {
      await load(uniqueKeys, "en")
    }
  }

  /**
   * Resolves localization for a specific field in an item
   * @param {Object} localizedObj - The object containing localization data
   * @param {Object} localizationItem - The localization details
   * @returns {string} The translated content
   */
  const resolveFieldLocalization = (localizedObj, localizationItem) => {
    const { localizationKey, field } = localizationItem
    let translation = t(localizationKey)
    if (translation === `{{${localizationKey}}}`) {
      translation = translateWithLanguage(localizationKey, "en")
    }
    
    // Only apply translation if we have a valid one
    if (translation && translation !== `{{${localizationKey}}}`) {
      // Try content field first, then root-level field
      if (localizedObj.content?.[field]) {
        localizedObj.content[field] = translation
      } else if (localizedObj[field] !== undefined) {
        localizedObj[field] = translation
      }
    }
  }

  /**
   * Recursively resolves localization for an item
   * @param {Object} item - The item to localize
   * @returns {Object} The localized item
   */
  const resolveLocalization = (item) => {
    const resolveItem = (obj) => {
      if (!obj) return obj

      const localizedObj = safeClone(obj)

      // Handle array of strings case
      if (Array.isArray(localizedObj)) {
        return localizedObj.map((item) => resolveItem(item))
      }

      // Process localization array if present
      if (
        Array.isArray(localizedObj.localization) &&
        localizedObj.localization.length
      ) {
        localizedObj.localization.forEach((localizationItem) => {
          resolveFieldLocalization(localizedObj, localizationItem)
        })
      }

      // Recursively process nested content
      if (localizedObj.content) {
        Object.entries(localizedObj.content).forEach(([key, value]) => {
          const isArrayOfStrings =
            Array.isArray(value) && value.length && typeof value[0] === "string"
          
          // Handle array of key-value objects (like texts and texts_mobile)
          const isKeyValueArray = Array.isArray(value) && value.length && 
            value[0] && typeof value[0] === "object" && 
            value[0].key && value[0].value !== undefined

          if (isKeyValueArray) {
            // Convert array of {key, value} objects to direct object properties
            const convertedObj = {}
            value.forEach(item => {
              if (item.key && item.value !== undefined) {
                convertedObj[item.key] = item.value
              }
            })
            localizedObj.content[key] = convertedObj
          } else if (!isArrayOfStrings && typeof value === "object") {
            localizedObj.content[key] = resolveItem(value)
          }
        })

        // Preserve ID in content if present
        if (localizedObj._id) localizedObj.content._id = localizedObj._id
      }

      // If there's an _id and content exists, return content to avoid field collision
      // Otherwise return the original fallback behavior
      if (localizedObj._id && localizedObj.content && typeof localizedObj.content === 'object') return localizedObj.content
      return localizedObj.content ?? localizedObj
    }

    return resolveItem(safeClone(item))
  }

  return {
    readLocalization,
    resolveLocalization,
  }
}

export default useCMSLocalization
